import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Badge } from '@/components/Shared/Badge';
import { SectionHead } from '@/components/Dashboard/SectionHead';
import type { OpenItemDto } from '@/store/useOpenItemsStore';
import { isoDate } from '@/utils/dateUtils';
import {
  formatDueDate,
  getOpenItemLink,
  getOpenItemText,
  isOverdue,
  isReport,
  isSystemItem,
} from '@/pages/OpenItems/openItemText';

/** Object and reporter of a report — enough for Verwaltung to place it at a glance. */
function reportMeta(item: OpenItemDto): string {
  return [item.objectName, item.createdByName].filter(Boolean).join(' · ');
}

interface Props {
  items: OpenItemDto[];
  /** Rows shown before the card defers to the full list. */
  maxRows?: number;
}

/**
 * Top of the caller's open items — manual, system and, for Verwaltung, reports — with a link to
 * the full list. Shared by the admin and the employee dashboard.
 */
export function OpenItemsCard({ items, maxRows = 6 }: Props) {
  const { t, i18n } = useTranslation(['openItems']);
  const todayIso = useMemo(() => isoDate(new Date()), []);
  const isEmpty = items.length === 0;

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      <SectionHead
        kicker={t('openItems:dashboard.kicker')}
        title={t('openItems:dashboard.title')}
        action={
          <div className="flex items-center gap-3">
            <span
              className={`font-mono-num text-xs px-2 py-0.5 rounded-full ${
                isEmpty
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              }`}
            >
              {items.length}
            </span>
            <Link
              to="/open-items"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-700 dark:text-primary-300"
            >
              {t('openItems:dashboard.all')}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden />
            </Link>
          </div>
        }
      />
      {isEmpty ? (
        <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/10 py-6 text-sm text-emerald-700 dark:text-emerald-200">
          <CheckCircle2 className="w-5 h-5" aria-hidden />
          {t('openItems:dashboard.empty')}
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-border-subtle dark:divide-slate-800">
          {items.slice(0, maxRows).map((item) => (
            <li key={item.id}>
              <Link
                to={getOpenItemLink(item) ?? '/open-items'}
                className="py-2.5 flex items-center gap-3 min-w-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 -mx-2 px-2 rounded-md transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink dark:text-slate-50 truncate">
                    {getOpenItemText(item, t, i18n.language)}
                  </div>
                  {(item.dueDate || isSystemItem(item) || isReport(item)) && (
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-muted dark:text-slate-400 min-w-0">
                      {isSystemItem(item) && (
                        <Badge variant="info">{t('openItems:badge.system')}</Badge>
                      )}
                      {isReport(item) && (
                        <>
                          <Badge color="purple">{t('openItems:badge.report')}</Badge>
                          <span className="truncate">{reportMeta(item)}</span>
                        </>
                      )}
                      {item.dueDate && (
                        <span
                          className={`tabular-nums ${
                            isOverdue(item.dueDate, todayIso) ? 'text-red-600 font-medium' : ''
                          }`}
                        >
                          {t('openItems:dashboard.due', { date: formatDueDate(item.dueDate) })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-ink-faint shrink-0" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
